import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { ROLES, ROLE_PERMISSIONS } from '../data/roles';
import { authFetch, dataFetch } from '../api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [authUser, setAuthUser] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const isReloggingIn = useRef(false);

  // Helper to map API user to our app's user object
  const mapUser = (apiUser, employeeRole = null) => {
    // Normalize "Learner" and "user" to our app's standard Employee role
    let rawRole = employeeRole || apiUser.role || ROLES.EMPLOYEE;
    if (rawRole === 'Learner' || rawRole === 'user') {
      rawRole = ROLES.EMPLOYEE;
    }

    return {
      id: apiUser.id,
      name: apiUser.name || 'User',
      email: apiUser.email,
      avatar: (apiUser.name || 'U').substring(0, 2).toUpperCase(),
      role: rawRole
    };
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        const res = await authFetch('get-session');
        if (res?.data?.user) {
          localStorage.setItem('user_id', res.data.user.id);
          
          let empRole = null;
          try {
            // Check if user has an employee record to override role
            const empRes = await dataFetch(`read/employees?user_id=${res.data.user.id}`);
            if (empRes?.data?.length > 0 && empRes.data[0].role) {
               empRole = empRes.data[0].role;
            } else if (res.data.user.email) {
               // Fallback: Check by email if user_id wasn't linked yet
               const emailRes = await dataFetch(`read/employees?email=${encodeURIComponent(res.data.user.email)}`);
               if (emailRes?.data?.length > 0) {
                   empRole = emailRes.data[0].role;
                   // Auto-link the user_id to the employee record
                   await dataFetch(`update/employees/${emailRes.data[0].id}`, {
                     method: 'PUT',
                     body: JSON.stringify({ ...emailRes.data[0], user_id: res.data.user.id })
                   }).catch(() => {});
               } else {
                 // Fallback to admin invites if not an employee
                 const adminRes = await dataFetch(`read/admin_invites?user_id=${res.data.user.id}`);
                 if (adminRes?.data?.length > 0 && adminRes.data[0].role) {
                     empRole = adminRes.data[0].role;
                 }
               }
            } else {
               // Fallback to admin invites if not an employee
               const adminRes = await dataFetch(`read/admin_invites?user_id=${res.data.user.id}`);
               if (adminRes?.data?.length > 0 && adminRes.data[0].role) {
                   empRole = adminRes.data[0].role;
               }
            }
          } catch(e) {
            // Ignore if role lookup fails
            console.warn('Could not fetch role record for user:', e);
          }
          
          const mapped = mapUser(res.data.user, empRole);
          setAuthUser(res.data.user);
          setCurrentUser(mapped);
        } else {
          localStorage.removeItem('user_id');
        }
      } catch (err) {
        localStorage.removeItem('user_id');
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();

    // Global listener: Attempt background relogin if any API call returns 401
    const handleSessionExpired = async () => {
      if (isReloggingIn.current) return;

      const savedEmail = localStorage.getItem('auth_email');
      const savedPassword = localStorage.getItem('auth_password');

      if (savedEmail && savedPassword) {
        isReloggingIn.current = true;
        try {
          const res = await authFetch('sign-in/email', {
            method: 'POST',
            body: JSON.stringify({ email: savedEmail, password: savedPassword })
          });

          if (res?.status === 'success') {
            // Background Relogin successful, keep user logged in and update session
            localStorage.setItem('user_id', res.data.user.id);
            isReloggingIn.current = false;
            
            // Dispatch event to resume queued API requests
            window.dispatchEvent(new Event('auth-session-restored'));
            return; 
          }
        } catch (e) {
          console.error("Background relogin failed", e);
        }
        isReloggingIn.current = false;
      }

      // If no credentials or relogin failed, proceed to logout and force redirect
      setAuthUser(null);
      setCurrentUser(null);
      localStorage.removeItem('user_id');
      localStorage.removeItem('auth_email');
      localStorage.removeItem('auth_password');
      
      // Dispatch event to reject queued API requests and drop them
      window.dispatchEvent(new Event('auth-session-failed'));
    };
    
    window.addEventListener('auth-session-expired', handleSessionExpired);

    return () => {
      window.removeEventListener('auth-session-expired', handleSessionExpired);
    };
  }, []);

  const login = async (user) => {
    setAuthUser(user);
    localStorage.setItem('user_id', user.id);
    
    let empRole = null;
    try {
      const empRes = await dataFetch(`read/employees?user_id=${user.id}`);
      if (empRes?.data?.length > 0 && empRes.data[0].role) {
         empRole = empRes.data[0].role;
      } else if (user.email) {
         const emailRes = await dataFetch(`read/employees?email=${encodeURIComponent(user.email)}`);
         if (emailRes?.data?.length > 0) {
             empRole = emailRes.data[0].role || null;
             // Auto-link the user_id to the employee record
             await dataFetch(`update/employees/${emailRes.data[0].id}`, {
               method: 'PUT',
               body: JSON.stringify({ ...emailRes.data[0], user_id: user.id })
             }).catch(() => {});
         } else {
           const adminRes = await dataFetch(`read/admin_invites?user_id=${user.id}`);
           if (adminRes?.data?.length > 0 && adminRes.data[0].role) {
               empRole = adminRes.data[0].role;
           }
         }
      } else {
         const adminRes = await dataFetch(`read/admin_invites?user_id=${user.id}`);
         if (adminRes?.data?.length > 0 && adminRes.data[0].role) {
             empRole = adminRes.data[0].role;
         }
      }
    } catch(e) {
      console.warn('Could not fetch role record for user:', e);
    }
    
    const mapped = mapUser(user, empRole);
    setCurrentUser(mapped);
    return mapped;
  };

  const logout = async () => {
    await authFetch('sign-out', { method: 'POST' });
    // Flag to inform the login screen to ignore the previous route
    sessionStorage.setItem('manual_logout', 'true');
    setAuthUser(null);
    setCurrentUser(null);
    localStorage.removeItem('user_id');
    localStorage.removeItem('auth_email');
    localStorage.removeItem('auth_password');
  };

  const hasPermission = (permission) => {
    if (!currentUser) return false;
    const perms = ROLE_PERMISSIONS[currentUser.role] || [];
    return perms.includes(permission);
  };

  const hasAnyPermission = (...permissions) => permissions.some(p => hasPermission(p));
  const hasAllPermissions = (...permissions) => permissions.every(p => hasPermission(p));

  const switchUser = () => {
    // Stub
  };

  return (
    <AuthContext.Provider value={{ 
      authUser, login, logout, isLoading,
      currentUser, hasPermission, hasAnyPermission, hasAllPermissions, switchUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};